import React, { useState, useEffect } from "react";
import { countryCodes } from "../common/constants";
import PhoneNumberInput from "../common/PhoneNumberInput";
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { updateUserName } from "../../../api/profileService";
import { useAuth } from "../../../context/AuthContext";

const PersonalInfoTab = ({ userInfo }) => {
  const { fetchProfile } = useAuth();
  const splitName = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const parts = fullName.trim().split(" ");
    const firstName = parts[0] || "";
    const lastName = parts.slice(1).join(" ") || "";
    return { firstName, lastName };
  };

  const { firstName, lastName } = splitName(userInfo?.name);
  const [phoneNumber, setPhoneNumber] = useState(userInfo?.phone || "");
  const [formData, setFormData] = useState({
    firstName: firstName,
    lastName: lastName,
    phone: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Update formData if userInfo changes
  useEffect(() => {
    const { firstName, lastName } = splitName(userInfo?.name);
    setFormData(prev => ({
      ...prev,
      firstName: firstName,
      lastName: lastName
    }));
  }, [userInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Validation function for name fields - only allows single letters
  const validateNameInput = (value) => {
    // Only allow letters (removed dots)
    return /^[A-Za-z]+$/.test(value);
  };

  const handleNameInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate input
    if (value === '' || validateNameInput(value)) {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error if it exists
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null
        });
      }
    } else {
      // Set error message
      setFormErrors({
        ...formErrors,
        [name]: "Only letters are allowed (no dots, spaces or special characters)"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    
    // Get actual values (either from form or from existing data)
    const firstNameValue = formData.firstName;
    const lastNameValue = formData.lastName;
    
    console.log("Submitting profile update:", { firstName: firstNameValue, lastName: lastNameValue });
    
    // Validate form
    const errors = {};
    if (!firstNameValue.trim()) {
      errors.firstName = "First name is required";
    } else if (!validateNameInput(firstNameValue)) {
      errors.firstName = "Only letters are allowed (no dots, spaces or special characters)";
    }
    
    if (lastNameValue.trim() && !validateNameInput(lastNameValue)) {
      errors.lastName = "Only letters are allowed (no dots, spaces or special characters)";
    }
    
    /* Phone validation temporarily commented out as it's not used
    if (!phoneNumber) {
      errors.phone = "Phone number is required";
    } else if (!isPossiblePhoneNumber(phoneNumber)) {
      errors.phone = "Please enter a valid phone number";
    }
    */
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Call the real API with the correct parameter names
      await updateUserName(firstNameValue, lastNameValue);
      
      // Immediately fetch the updated profile to update UI
      await fetchProfile();
      
      setSubmitSuccess(true);
      setFormErrors({});
    } catch (error) {
      console.error("Error updating profile:", error);
      if (error.response && error.response.data && error.response.data.message) {
        setFormErrors({ 
          submit: error.response.data.message 
        });
      } else {
        setFormErrors({ 
          submit: "Failed to update profile. Please try again." 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-6">Personal Information</h2>
      
      {submitSuccess && (
        <div className="bg-green-600 border border-green-700 text-white px-4 py-3 rounded relative mb-4 dark:bg-green-700 dark:text-white dark:border-green-900">
          <span className="block sm:inline">Profile updated successfully!</span>
        </div>
      )}
      
      {formErrors.submit && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 dark:bg-red-900 dark:text-red-100 dark:border-red-800">
          <span className="block sm:inline">{formErrors.submit}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
            <input
              type="text"
              name="firstName"
              className={`w-full px-4 py-3 rounded-lg border ${formErrors.firstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Your first name"
              value={formData.firstName}
              onChange={handleNameInputChange}
            />
            {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
            <input
              type="text"
              name="lastName"
              className={`w-full px-4 py-3 rounded-lg border ${formErrors.lastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Your last name"
              value={formData.lastName}
              onChange={handleNameInputChange}
            />
            {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
            <input
              type="email"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white"
              value={userInfo?.email || ""}
              readOnly
            />
            <span className="text-xs text-gray-500 mt-1 inline-block">Email cannot be changed</span>
          </div>
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile Number</label>
            <PhoneNumberInput 
              value={phoneNumber}
              onChange={setPhoneNumber}
              defaultCountry="IN"
              placeholder="Your mobile number"
            />
            {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
          </div> */}
          <div className="md:col-span-2 flex justify-end">
            <button 
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-3 mt-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoTab; 